<i18n>
{
  "de": {
    "about": "Über",
    "hide": "Ausblenden"
  },
  "en": {
    "about": "About",
    "hide": "Hide"
  }
}
</i18n>

<template>
  <div
    v-if="ready"
    id="sidenav"
  >
    <!-- About Button (when collapsed on mobile) -->
    <div
      v-if="isMobile && !isExpanded"
      class="profile-toggle"
      @click="showProfile"
    >
      <div class="about-button">
        <span>{{ $t('about') }}</span>
      </div>
    </div>

    <!-- Profile Box -->
    <div
      class="profile-container"
      :class="profileContainerClasses"
    >
      <profileBox :user="artist" />

      <!-- Hide Button (when expanded on mobile) -->
      <div
        v-if="isMobile && isExpanded"
        class="profile-hide-button"
        @click="hideProfile"
      >
        <span>{{ $t('hide') }}</span>
      </div>
    </div>

    <portal :to="menuOpen">
      <followBox v-if="isAuthenticated" />
    </portal>
    <portal-target name="desktopFilterPosts" />
    <portal-target name="desktopFilterShop" />
  </div>
</template>

<script>
import { mapState } from 'vuex'
import profileBox from '~/components/profile-box'
import followBox from '~/components/follow-box'

export default {
  components: {
    profileBox,
    followBox
  },

  data() {
    return {
      ready: false,
      isExpanded: true, // Default to expanded
      isProfileHidden: false
    }
  },

  computed: {
    ...mapState(['artist', 'isAuthenticated', 'mobileMenuOpen']),
    menuOpen() {
      return this.mobileMenuOpen ? 'mobileFollowship' : 'desktopFollowship'
    },
    isMobile() {
      return this.$mq === 'sm' || this.$mq === 'md'
    },
    profileContainerClasses() {
      if (!this.isMobile) {
        return 'profile-container--always-visible'
      }
      return {
        'profile-container--expanded': this.isExpanded,
        'profile-container--collapsed': !this.isExpanded
      }
    }
  },

  methods: {
    hideProfile() {
      if (this.isMobile) {
        this.isExpanded = false
        // Store hidden preference in localStorage
        if (process.client) {
          localStorage.setItem(`profileHidden_${this.artist._id}`, 'true')
        }
      }
    },
    showProfile() {
      if (this.isMobile) {
        this.isExpanded = true
        // Remove hidden preference from localStorage
        if (process.client) {
          localStorage.removeItem(`profileHidden_${this.artist._id}`)
        }
      }
    },
    checkIfProfileHidden() {
      if (process.client && this.isMobile) {
        const isHidden = localStorage.getItem(`profileHidden_${this.artist._id}`) === 'true'
        this.isProfileHidden = isHidden
        this.isExpanded = !isHidden
        console.log('Profile hidden preference:', isHidden, 'isExpanded:', this.isExpanded)
      }
    }
  },

  mounted() {
    this.checkIfProfileHidden()
    this.ready = true
  }
}
</script>

<style lang="scss" scoped>
@import "assets/sass/_variables";

#sidenav {
  position: relative;
}

// Profile container with collapsible functionality
.profile-container {
  position: relative;
  overflow: hidden;
  transition: all 0.4s ease-out;

  // Always visible on desktop by default
  @media screen and (min-width: #{$tablet}) {
    height: auto;
    min-height: auto;
    padding: 0;

    :deep(.box) {
      opacity: 1;
      transform: translateY(0);
      pointer-events: all;
    }
  }

  // Mobile states
  @media screen and (max-width: #{$mobile}) {
    // Expanded state (default on mobile)
    max-height: 1000px;
    padding: 0;

    :deep(.box) {
      opacity: 1;
      transform: translateY(0);
      pointer-events: all;
    }
  }

  // Collapsed state on mobile
  &--collapsed {
    @media screen and (max-width: #{$mobile}) {
      max-height: 60px !important;
      padding: 0;

      :deep(.box) {
        opacity: 0 !important;
        transform: translateY(-100%) !important;
        pointer-events: none !important;
      }
    }
  }

  // Always visible state (desktop)
  &--always-visible {
    height: auto;
    min-height: auto;
    padding: 0;

    :deep(.box) {
      opacity: 1;
      transform: translateY(0);
      pointer-events: all;
    }
  }

  :deep(.box) {
    transition: all 0.4s ease-out;
    transform-origin: top center;
  }
}

// Toggle button for mobile
.profile-toggle {
  position: absolute;
  top: 10px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1;
  cursor: pointer;
  transition: all 0.3s ease-out;
  opacity: 1;

  &--hidden {
    opacity: 0;
    transform: translateX(-50%) scale(0.8);
    pointer-events: none;
  }

  .about-button {
    background-color: var(--primary-color, #007bff);
    color: white;
    padding: 8px 16px;
    border-radius: 20px;
    font-size: 0.9rem;
    font-weight: 600;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
    transition: all 0.3s ease-out;

    &:hover {
      background-color: var(--primary-color-hover, #0056b3);
      box-shadow: 0 6px 16px rgba(0, 0, 0, 0.3);
      transform: translateY(-2px);
    }
  }
}

// Hide button inside profile box
.profile-hide-button {
  position: absolute;
  top: 10px;
  right: 10px;
  cursor: pointer;
  background-color: rgba(0, 0, 0, 0.6);
  color: white;
  padding: 6px 12px;
  border-radius: 15px;
  font-size: 0.8rem;
  font-weight: 500;
  transition: all 0.3s ease-out;
  z-index: 2;

  &:hover {
    background-color: rgba(0, 0, 0, 0.8);
    transform: translateY(-1px);
  }

  @media screen and (min-width: #{$tablet}) {
    display: none;
  }
}

// Hide toggle button on desktop
@media screen and (min-width: #{$tablet}) {
  .profile-toggle {
    display: none;
  }
}
</style>
